import React, { useEffect, useMemo, useState } from 'react';
import AppCard from '../components/AppCard';
import { fetchApps, fetchAppStatus } from '../services/api';

const DashboardPage = ({ email, onLogout }) => {
  const [apps, setApps] = useState([]);
  const [statusById, setStatusById] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [list, statuses] = await Promise.all([
          fetchApps(),
          fetchAppStatus().catch(() => []),
        ]);
        if (cancelled) return;
        setApps(list);
        const map = {};
        (statuses || []).forEach((row) => {
          map[row.id] = row.status;
        });
        setStatusById(map);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((app) => {
      if (category !== 'all' && app.category !== category) return false;
      if (!q) return true;
      return (
        app.name.toLowerCase().includes(q) ||
        app.description.toLowerCase().includes(q) ||
        app.category.toLowerCase().includes(q)
      );
    });
  }, [apps, query, category]);

  const counts = useMemo(() => {
    const values = apps.map((app) => statusById[app.id] || 'unknown');
    return {
      up: values.filter((s) => s === 'up').length,
      down: values.filter((s) => s === 'down').length,
      unknown: values.filter((s) => s === 'unknown').length,
    };
  }, [apps, statusById]);

  return (
    <div className="min-h-screen bg-[#07080d] text-zinc-100 font-mono">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-cyan-400/10 px-6 py-4">
        <span className="tracking-[0.18em] text-teal-300">SHOWME.TIPS</span>
        <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-400">
          <span className="text-emerald-400">{counts.up} up</span>
          <span className="text-rose-400">{counts.down} down</span>
          <span className="text-amber-400">{counts.unknown} unknown</span>
          <span>{email}</span>
          <button type="button" onClick={onLogout} className="text-teal-200">
            Log out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-8">
        <input
          aria-label="Search apps"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search apps…"
          className="mb-4 w-full rounded-full border border-cyan-400/20 bg-[#111827] px-5 py-3 text-sm text-zinc-100 outline-none"
        />
        <div className="mb-6 flex flex-wrap gap-2">
          {['all', 'home', 'media', 'cloud', 'dev'].map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => setCategory(chip)}
              className={`rounded-full px-3 py-1 text-xs uppercase ${
                category === chip
                  ? 'bg-cyan-400/20 text-teal-200 border border-cyan-400/40'
                  : 'bg-[#111827] text-zinc-400 border border-transparent'
              }`}
            >
              {chip === 'all' ? 'All' : chip}
            </button>
          ))}
        </div>
        {loadError && <p className="text-rose-400">Couldn’t load apps.</p>}
        {!loadError && visible.length === 0 && (
          <p className="text-zinc-500">No apps match.</p>
        )}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {visible.map((app) => (
            <AppCard key={app.id} app={app} status={statusById[app.id] || 'unknown'} />
          ))}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
